import { lazy } from "react";
import NotFoundPage from "./features/not-found/NotFoundPage.jsx";
import Shell, { HeaderLessShell, MiniShell } from "./features/shell/Shell.jsx";
import SparkShellWithWorkshopTourProvider from "./features/workshops/user-tour/SparkShellWithWorkshopTourProvider.jsx";
import DemoShell from "./features/demo/DemoShell";
import AuthShell from "./features/auth/AuthShell";
import { flags } from "./flags";

export const ROUTES = {
  empty: {
    scope: "public",
    allowMobile: true,
    path: "/",
    getUrl: () => "/",
    Component: lazy(() => import("./features/auth/EmptyPage")),
    Layout: HeaderLessShell,
  },
  login: {
    scope: "public",
    allowMobile: true,
    path: "/login",
    getUrl: () => `/login`,
    Component: lazy(() => import("./features/auth/AuthPage")),
    Layout: AuthShell,
  },
  loginIba: {
    scope: "public",
    allowMobile: true,
    path: "/login/iba-Fortbildung",
    getUrl: () => `/login/iba-Fortbildung`,
    Component: lazy(() => import("./features/one-off/iba/Login/LoginPage.jsx")),
    Layout: Shell,
  },
  registerPage: {
    scope: "public",
    allowMobile: true,
    path: "/r/:secret",
    getUrl: (secret) => `/r/${secret}`,
    Component: lazy(() => import("./features/auth/AuthPage")),
    Layout: AuthShell,
  },
  postRegisterPage: {
    scope: "user",
    allowMobile: true,
    path: "/register/:secret/success",
    getUrl: (secret) => `/register/${secret}/success`,
    Component: lazy(
      () => import("./features/auth/RegisterSuccess/RegisterSuccess"),
    ),
    Layout: AuthShell,
  },
  addSpark: {
    scope: "public",
    path: "/add-spark/:sparkSlug",
    getUrl: (sparkSlug) => `/add-spark/${sparkSlug}`,
    Component: lazy(() => import("./features/auto-add-spark/AddSparkPage.jsx")),
    Layout: MiniShell,
  },
  forgotPasswordPage: {
    scope: "public",
    allowMobile: true,
    path: "/auth/forgot-password/:token?",
    getUrl: () => `/auth/forgot-password`,
    Component: lazy(
      () => import("./features/auth/PasswordReset/ForgotPasswordForTeachers"),
    ),
    Layout: AuthShell,
  },
  forgotPasswordPageForStudents: {
    scope: "public",
    allowMobile: true,
    path: "/auth/how-to-reset-student-pw",
    getUrl: () => "/auth/how-to-reset-student-pw",
    Component: lazy(
      () => import("./features/auth/PasswordReset/ForgotPasswordForStudents"),
    ),
    Layout: AuthShell,
  },
  profile: {
    scope: "user",
    path: "/profile",
    getUrl: () => `/profile`,
    Component: lazy(() => import("./features/auth/Profile/Profile")),
    Layout: Shell,
  },
  ...(flags.handbook && {
    handbook: {
      scope: "user",
      path: "/handbook",
      getUrl: () => "/handbook",
      Component: lazy(
        () => import("./features/workshops/handbook/HandbookOverview"),
      ),
      Layout: Shell,
    },
  }),

  workshopStart: {
    scope: "user",
    path: "/w/:slug",
    getUrl: (workshopSlug) => `/w/${workshopSlug}`,
    Component: lazy(
      () => import("./features/workshops/routing/RedirectToSpark.jsx"),
    ),
    Layout: Shell,
  },

  spark: {
    scope: "user",
    path: "/w/:workshopSlug/s/:sparkSlug/*",
    getUrl: ({ workshopSlug, sparkSlug, withTour = false }) =>
      `/w/${workshopSlug}/s/${sparkSlug}${withTour ? "?tour=true" : ""}`,
    Layout: SparkShellWithWorkshopTourProvider,
    noSuspense: true,
  },

  lernreisen: {
    scope: "user",
    path: "/lernreisen",
    getUrl: () => "/lernreisen",
    Layout: HeaderLessShell,
  },
  lernreisenOverview: {
    scope: "user",
    path: null,
    /**
     * `showDashboard` suppresses the redirect a student with a single Lernreise
     * would otherwise get straight back into their spark — set it wherever the
     * user asked for the dashboard itself rather than just being sent "home".
     */
    getUrl: ({ showDashboard = false } = {}) =>
      `/lernreisen${showDashboard ? "?dashboard=true" : ""}`,
    Component: lazy(() => import("./features/workshops")),
    Layout: MiniShell,
    parent: "lernreisen",
    index: true,
  },
  lernreisenCreate: {
    scope: "user",
    path: "create",
    getUrl: () => "/lernreisen/create",
    Component: lazy(() => import("./features/workshops/create-workshop")),
    Layout: MiniShell,
    parent: "lernreisen",
  },

  admin: {
    scope: "admin",
    path: "/admin",
    getUrl: () => "/admin",
    Layout: Shell,
  },
  admins: {
    scope: "admin",
    path: "admins",
    getUrl: () => "/admin/admins",
    Component: lazy(() => import("./features/admin/AdminUsersPage")),
    Layout: MiniShell,
    parent: "admin",
  },
  templates: {
    scope: "admin",
    path: "templates",
    getUrl: () => "/admin/templates",
    Component: lazy(() => import("./features/admin/Templates.jsx")),
    Layout: MiniShell,
    parent: "admin",
  },
  administrateWorkshops: {
    scope: "admin",
    path: "workshops",
    getUrl: () => "/admin/workshops",
    Component: lazy(() => import("./features/admin/workshops/Workshops")),
    Layout: MiniShell,
    parent: "admin",
  },
  workshopDetail: {
    scope: "admin",
    path: ":slug",
    getUrl: (slug) => `/admin/workshops/${slug}`,
    Component: lazy(() => import("./features/admin/workshops/WorkshopDetail")),
    Layout: MiniShell,
    parent: "administrateWorkshops",
  },
  allComments: {
    scope: "admin",
    path: "moderate",
    getUrl: () => `/admin/moderate`,
    Component: lazy(() => import("./features/admin/ModerateComments")),
    Layout: MiniShell,
    parent: "admin",
  },
  managePublicShowrooms: {
    scope: "admin",
    path: "public-showrooms",
    getUrl: () => `/admin/public-showrooms`,
    Component: lazy(() => import("./features/admin/ManagePublicShowrooms")),
    Layout: MiniShell,
    parent: "admin",
  },

  demoHome: {
    scope: "public",
    path: "/demo",
    getUrl: () => "/demo",
    Component: lazy(() => import("./features/demo")),
    index: true,
    Layout: HeaderLessShell,
  },
  demoLernreise: {
    scope: "public",
    path: "/demo/:sparkSlug/*",
    getUrl: ({ sparkSlug }) => `/demo/${sparkSlug}`,
    Layout: DemoShell,
    // DemoShell self-hosts its own boundary/suspense (its Layout is the suspending shell).
    noSuspense: true,
  },

  notFoundPage: {
    scope: "public",
    allowMobile: true,
    path: "not-found",
    getUrl: () => "/not-found",
    Component: NotFoundPage,
    Layout: Shell,
  },
  notFound: {
    scope: "public",
    allowMobile: true,
    path: "*",
    Component: NotFoundPage,
    Layout: Shell,
  },
};

/**
 * URLs for the routes SparkShell mounts below a spark. They can't be ROUTES
 * entries: `computeRoutesList` turns every entry into a real route, and these
 * are mounted by SparkShell's own `useRoutes`. So they hang off the spark's own
 * `getUrl` instead, and take the same `{ workshopSlug, sparkSlug }` it does.
 */
export const SPARK_ROUTES = {
  showroom: ({ workshopSlug, sparkSlug }) =>
    `${ROUTES.spark.getUrl({ workshopSlug, sparkSlug })}/showroom`,
};
